import React, { useMemo } from 'react'
import classNames from 'classnames'
import { Text } from '@/atoms/Text'
import { useIncrementer } from '@/services/GoalService/hooks/useIncrementer'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import useTheatricalGoalService from '@/views/TicketCheckoutViews/hooks/useTheatricalGoalService'

interface Props {
  theatricalSlug: string
  releaseDate: string
}

export const TicketGoalLine: React.FC<Props> = ({ theatricalSlug, releaseDate }) => {
  const { locale } = useLocale()
  const { t } = useTranslate('tickets')

  const { goalLineInfo } = useTheatricalGoalService({
    theatricalSlug,
    releaseDate,
  })

  const numberFormat = new Intl.NumberFormat(locale)
  const goalDisplay = useMemo(
    () => Intl.NumberFormat(locale).format(goalLineInfo?.currentMilestone),
    [goalLineInfo?.currentMilestone, locale],
  )
  const incrementerNumber = useIncrementer(goalLineInfo?.ticketsSold, 0.9)
  const incrementerPercent = Math.floor((incrementerNumber / goalLineInfo?.currentMilestone) * 100)
  const ticketsSold = Math.ceil(incrementerNumber)
  const goalPercent = incrementerPercent

  return (
    <div className="w-full">
      <div className="flex items-center justify-between text-core-gray-200">
        <Text color="core-gray-400" className="photon-caption-xs md:photon-caption-sm">
          {t('numberOfTicketsTheatrical', '{{numTickets}} Tickets', {
            numTickets: numberFormat.format(ticketsSold),
          })}
        </Text>

        <Text color="core-gray-400" className="photon-caption-xs md:photon-caption-sm">
          {!goalLineInfo?.isLastMilestone
            ? t('milestoneGoalTickets', `Milestone: {{ goalDisplay }}`, {
                goalDisplay,
              })
            : t('goalDisplayGoalTickets', ` {{ goalDisplay }} Goal`, {
                goalDisplay,
              })}
        </Text>
      </div>

      <ProgressBar percent={Math.min(goalPercent, 100)} className="my-1.5" />

      <div className="flex items-center justify-between text-core-gray-200">
        <Text color="core-gray-400" className="photon-caption-xs md:photon-caption-sm">
          {goalLineInfo?.isLastMilestone
            ? t('progressOfTicketGoalTicketsOfGoalTickets', `{{progress}}% of Goal`, {
                progress: goalPercent,
              })
            : t('progressOfTicketGoalTicketsPageTickets', `{{progress}}% of Milestone`, {
                progress: goalPercent,
              })}
        </Text>
      </div>
    </div>
  )
}

const ProgressBar = ({ percent, className }: { percent: number; className?: string }) => {
  return (
    <div className={classNames('w-full rounded-full bg-white/20 backdrop-blur-[56px]', className)}>
      <div
        className="h-[6px] rounded-full bg-oxide-bright lg:h-2"
        style={{
          width: `${percent}%`,
        }}
      />
    </div>
  )
}
